import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/i18n';
import { Menu, X, ChevronDown, LogOut, LayoutDashboard, User, GraduationCap } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

import { OFALogo } from './OFALogo';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const NAV_LINKS = [
    { to: '/', label: t('nav.home') },
    { to: '/vision', label: t('nav.vision') },
    { to: '/programs', label: t('nav.programs'), dropdown: [
      { to: '/programs', label: lang === 'fr' ? 'Explorer les programmes' : 'Explore Programs' },
      { to: '/summer-school', label: t('nav.summer') },
    ]},
    { to: '/perspectives', label: t('nav.perspectives') },
    { to: '/certificates', label: t('nav.certificates') },
    { to: '/educators', label: t('nav.educators') },
    { to: '/contribute', label: t('nav.contribute') },
  ];

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  const isHomePage = location.pathname === '/';
  const useLightText = isHomePage && !scrolled;

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHomePage
          ? 'backdrop-blur-xl bg-white/95 border-b border-slate-200 shadow-sm'
          : 'bg-[#2D78F6]'
      }`}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <OFALogo variant={useLightText ? 'light' : 'dark'} className="h-14 md:h-16" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => {
              const active = location.pathname === link.to || (link.dropdown && link.dropdown.some(d => d.to === location.pathname));
              if (link.dropdown) {
                return (
                  <DropdownMenu key={link.to}>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`nav-${link.to.replace('/', '') || 'home'}-trigger`}
                        className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors duration-200 inline-flex items-center gap-1 ${
                          active
                            ? 'text-[#2D78F6] bg-[#2D78F6]/10'
                            : useLightText
                              ? 'text-white/90 hover:text-white hover:bg-white/10'
                              : 'text-[#14274B] hover:text-[#2D78F6] hover:bg-[#14274B]/5'
                        }`}
                      >
                        {link.label}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                      {link.dropdown.map((d) => (
                        <DropdownMenuItem key={d.to} asChild>
                          <Link to={d.to} data-testid={`nav-dd-${d.to.replace('/', '')}`}>{d.label}</Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-${link.to.replace('/', '') || 'home'}`}
                  className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors duration-200 ${
                    active
                      ? 'text-[#2D78F6] bg-[#2D78F6]/10'
                      : useLightText
                        ? 'text-white/90 hover:text-white hover:bg-white/10'
                        : 'text-[#14274B] hover:text-[#2D78F6] hover:bg-[#14274B]/5'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          <div className="hidden md:flex items-center gap-2">
            {/* Language Toggle */}
            <button
              onClick={() => setLang(lang === 'en' ? 'fr' : 'en')}
              data-testid="lang-toggle"
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors duration-200 ${
                useLightText
                  ? 'border-white/20 text-white/90 hover:bg-white/10'
                  : 'border-slate-200 text-[#14274B] hover:bg-[#14274B]/5'
              }`}
            >
              {lang === 'en' ? 'FR' : 'EN'}
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-trigger"
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm transition-colors duration-200 ${useLightText ? 'text-white/90 hover:bg-white/10' : 'text-[#14274B] hover:bg-[#14274B]/5'}`}
                  >
                    <User className="w-4 h-4" />
                    {user.name}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {user.role === 'student' && (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" data-testid="nav-dashboard" className="flex items-center gap-2">
                        <GraduationCap className="w-4 h-4" /> {t('nav.dashboard')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-2">
                        <LayoutDashboard className="w-4 h-4" /> {t('nav.admin')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 text-[#EA5658]">
                    <LogOut className="w-4 h-4" /> {t('nav.signout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login"
                className="px-6 py-2.5 text-sm font-medium bg-[#2D78F6] text-white rounded-sm hover:bg-[#1A5BB5] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                {t('nav.signin')}
              </Link>
            )}
          </div>

          {/* Mobile toggle */}
          <div className="flex items-center gap-2 md:hidden">
            <button
              onClick={() => setLang(lang === 'en' ? 'fr' : 'en')}
              data-testid="lang-toggle-mobile"
              className={`px-2 py-1 text-xs font-semibold rounded-sm border ${useLightText ? 'border-white/20 text-white/90' : 'border-slate-200 text-[#14274B]'}`}
            >
              {lang === 'en' ? 'FR' : 'EN'}
            </button>
            <button
              onClick={() => setOpen(!open)}
              className={`p-2 ${useLightText ? 'text-white' : 'text-[#14274B]'}`}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {open && (
          <div className="md:hidden pb-6 border-t border-slate-200 bg-white rounded-b-lg" data-testid="mobile-menu">
            <div className="flex flex-col gap-1 pt-4">
              {NAV_LINKS.map((link) => (
                <div key={link.to}>
                  <Link
                    to={link.to}
                    className={`px-4 py-3 text-sm font-medium rounded-sm block ${
                      location.pathname === link.to ? 'text-[#2D78F6] bg-[#2D78F6]/10' : 'text-[#14274B]'
                    }`}
                  >
                    {link.label}
                  </Link>
                  {link.dropdown && link.dropdown.filter(d => d.to !== link.to).map((d) => (
                    <Link
                      key={d.to}
                      to={d.to}
                      className={`pl-8 pr-4 py-2 text-sm rounded-sm block ${
                        location.pathname === d.to ? 'text-[#2D78F6] bg-[#2D78F6]/10' : 'text-slate-600'
                      }`}
                    >
                      ↳ {d.label}
                    </Link>
                  ))}
                </div>
              ))}
              {user ? (
                <>
                  {user.role === 'student' && (
                    <Link to="/dashboard" className="px-4 py-3 text-sm font-medium text-[#14274B]">{t('nav.dashboard')}</Link>
                  )}
                  {user.role === 'admin' && (
                    <Link to="/admin" className="px-4 py-3 text-sm font-medium text-[#14274B]">{t('nav.admin')}</Link>
                  )}
                  <button onClick={logout} className="px-4 py-3 text-sm font-medium text-left text-[#EA5658]">{t('nav.signout')}</button>
                </>
              ) : (
                <Link to="/login" className="mx-4 mt-2 px-6 py-2.5 text-sm font-medium text-center bg-[#2D78F6] text-white rounded-sm">{t('nav.signin')}</Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
